<template>
  <div class="content-wrapper">
    <section class="content-header page-hdr">
      <div class="page-hdr-left">
        <h1>{{ action }} AI Audience</h1>
        <span class="cust-ctx-pill" v-if="showContext && context.customer">{{ context.customer }}<template v-if="context.country"> · {{ context.country }}</template></span>
      </div>
      <ol class="breadcrumb">
        <li>
          <a href="#"><i class="fa fa-dashboard"></i> Home</a>
        </li>
        <li>Audience Builder</li>
        <li class="active">Generate AI Audience</li>
      </ol>
    </section>

    <div class="page-tab-bar" v-if="!editing">
      <div class="page-tab" :class="{ active: pageTab === 'build' }" @click="pageTab = 'build'">🧠 Build Audience</div>
      <div class="page-tab" :class="{ active: pageTab === 'library' }" @click="pageTab = 'library'">
        📚 Segment Library <span class="pt-count" v-if="segmentCount !== null">{{ segmentCount }}</span>
      </div>
    </div>

    <section class="content">
      <div class="row">
        <div class="col-xs-12">
          <div class="box">
            <div class="box-body">
              <div class="col-xs-12 col-md-12 col-lg-12">

                <div v-show="showBuild">
                  <div class="tab-bar" v-if="!editing">
                    <div class="tab" :class="{ active: mode === 'build' }" @click="mode = 'build'">🧠 Build with filters</div>
                    <div class="tab" :class="{ active: mode === 'upload' }" @click="mode = 'upload'">⬆ Upload CSV</div>
                  </div>

                  <Form v-show="mode === 'build'" :key="formKey" @segment-saved="onSegmentSaved" @context-changed="context = $event" />
                  <template v-if="mode !== 'build'">
                    <div class="upload-sub-toggle">
                      <button
                        type="button"
                        class="upload-sub-chip"
                        :class="{ 'upload-sub-chip-active': uploadType === 'existing' }"
                        @click="uploadType = 'existing'">Existing users (Account ID CSV)</button>
                      <button
                        type="button"
                        class="upload-sub-chip"
                        :class="{ 'upload-sub-chip-active': uploadType === 'new' }"
                        @click="uploadType = 'new'">New users (Google Sheet)</button>
                    </div>
                    <UploadAudiencePanel v-if="uploadType === 'existing'" @segment-saved="segmentsRefreshToken++" />
                    <NewUsersUploadPanel v-else @segment-saved="segmentsRefreshToken++" />
                  </template>
                </div>

                <!-- Always mounted (hidden when not active) so the tab count loads up front and the Build form keeps its state. -->
                <SegmentLibrary v-show="!showBuild" :refresh-token="segmentsRefreshToken" @count-changed="segmentCount = $event" />

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Form from './form.vue'
import UploadAudiencePanel from './UploadAudiencePanel.vue'
import NewUsersUploadPanel from './NewUsersUploadPanel.vue'
import SegmentLibrary from './SegmentLibrary.vue'

export default {
  name: 'ai-audience-builder',
  components: {
    Form,
    UploadAudiencePanel,
    NewUsersUploadPanel,
    SegmentLibrary
  },
  data () {
    return {
      action: 'Generate',
      pageTab: 'build',
      mode: 'build',
      uploadType: 'existing',
      editing: false,
      segmentsRefreshToken: 0,
      segmentCount: null,
      context: {},
      formKey: 0
    }
  },
  computed: {
    showBuild () {
      return this.pageTab === 'build' || this.editing
    },
    showContext () {
      return this.showBuild && this.mode === 'build'
    }
  },
  watch: {
    // Edit links point at this same route with only ?queryId changed, so the component is reused:
    // react to the query instead of relying on mounted().
    '$route.query.queryId' () {
      this.syncFromRoute()
      this.formKey++
    }
  },
  mounted () {
    this.syncFromRoute()
  },
  methods: {
    syncFromRoute () {
      if (this.$route.query.queryId) {
        this.action = 'Edit'
        this.editing = true
        this.mode = 'build'
        this.pageTab = 'build'
      } else {
        this.action = 'Generate'
        this.editing = false
      }
    },
    onSegmentSaved () {
      this.segmentsRefreshToken++
      // After an edit save, leave edit mode and drop back to the library so the updated segment is
      // visible; clear ?queryId so a later "Build" isn't stuck re-loading the edited segment.
      if (this.editing) {
        this.editing = false
        this.action = 'Generate'
        this.pageTab = 'library'
        if (this.$route.query.queryId) this.$router.replace({ path: '/audience-builder/ai-generate' })
      }
    }
  }
}
</script>

<style scoped>
.box-body {
  min-height: calc(100vh - 171px);
}

.page-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.page-hdr-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.cust-ctx-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #E6F5F4;
  border: 1px solid rgba(13, 148, 136, 0.25);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  color: #0B7C72;
  font-weight: 500;
  white-space: nowrap;
}

.page-tab-bar {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  background: #fff;
  padding: 0 15px;
}
.page-tab {
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #9e9e9e;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.page-tab:hover {
  color: #616161;
}
.page-tab.active {
  color: #e8305a;
  border-bottom-color: #e8305a;
  font-weight: 600;
}
.pt-count {
  background: #0D9488;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
}

.tab-bar {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 16px;
}
.tab {
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #9e9e9e;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.tab:hover {
  color: #616161;
}
.tab.active {
  color: #e8305a;
  border-bottom-color: #e8305a;
  font-weight: 600;
}

.upload-sub-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.upload-sub-chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #4a5568;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.upload-sub-chip:hover {
  border-color: #7DD3C8;
}
.upload-sub-chip-active {
  background: #0D9488;
  border-color: #0D9488;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// index.vue?4b58d756